import axios, {
  AxiosError,
  AxiosInstance,
  AxiosRequestConfig,
  AxiosResponse,
} from "axios";

const BASE_URL = process.env.GATSBY_BACKEND_URL;

export const api: AxiosInstance = axios.create({
  withCredentials: false,
  baseURL: `${BASE_URL}/api/v1`,
  paramsSerializer: {
    indexes: null,
  },
});

const getAccessToken = (): string => {
  try {
    return JSON.parse(localStorage.getItem("access") || "");
  } catch {
    return "";
  }
};

const getRefreshToken = (): string => {
  try {
    return JSON.parse(localStorage.getItem("refresh") || "");
  } catch {
    return "";
  }
};

// Define custom request with retry flag
interface RetryableRequest extends AxiosRequestConfig {
  _retry?: boolean;
}

// Define token response interface
interface TokenResponse {
  access: string;
  refresh: string;
}

// Define queue items interface
interface QueueItem {
  resolve: (value: string | PromiseLike<string>) => void;
  reject: (reason?: any) => void;
}

const isAuthRefreshCandidate = (error: AxiosError): boolean => {
  const status = error.response?.status;
  const code = (error.response?.data as { code?: string } | undefined)?.code;

  if (status === 401) {
    return true;
  }

  if (status !== 403) {
    return false;
  }

  return code === "auth_required" || code === "authentication_failed" || code === "token_not_valid";
};

api.interceptors.request.use(
  (request) => {
    // add the access token to the request
    if (getAccessToken()) {
      request.headers = request.headers || {};
      request.headers["Authorization"] = `Bearer ${getAccessToken()}`;
    }
    return request;
  },
  (error: AxiosError): Promise<AxiosError> => {
    return Promise.reject(error);
  }
);

// Track refresh token state
let isRefreshing: boolean = false;
let refreshPromise: Promise<AxiosResponse<TokenResponse>> | null = null;
let failedQueue: QueueItem[] = [];

// Process failed queue with new token
const processQueue = (
  error: AxiosError | null,
  token: string | null = null
): void => {
  failedQueue.forEach((prom) => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token as string);
    }
  });

  failedQueue = [];
};

api.interceptors.response.use(
  (response: AxiosResponse): AxiosResponse => response,
  async (error: AxiosError): Promise<any> => {
    // get the original request
    const originalRequest = error.config as RetryableRequest;

    // Prevent infinite refresh loops
    if (
      originalRequest.url &&
      originalRequest.url.includes("/auth/token/refresh/")
    ) {
      return Promise.reject(error);
    }

    // if the error is 401 (no credentials) or 403 (credentials invalid) and we have a refresh token
    if (
      error.response &&
      isAuthRefreshCandidate(error) &&
      getRefreshToken() &&
      !originalRequest._retry // Prevent retry loops
    ) {
      // Mark this request as retried
      originalRequest._retry = true;

      // If we're already refreshing, queue this request
      if (isRefreshing) {
        return new Promise<string>((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        })
          .then((token) => {
            originalRequest.headers = originalRequest.headers || {};
            originalRequest.headers["Authorization"] = `Bearer ${token}`;
            return api(originalRequest);
          })
          .catch((err) => {
            return Promise.reject(err);
          });
      }

      isRefreshing = true;

      try {
        // If no refresh in progress, create a new refresh promise
        if (!refreshPromise) {
          refreshPromise = api.post<TokenResponse>(
            `${BASE_URL}/auth/token/refresh/`,
            {
              refresh: getRefreshToken(),
            }
          );
        }

        // Wait for refresh response
        const response = await refreshPromise;

        // Update tokens in localStorage
        localStorage.setItem("access", JSON.stringify(response.data.access));
        localStorage.setItem("refresh", JSON.stringify(response.data.refresh));

        // Update current request's auth header
        originalRequest.headers = originalRequest.headers || {};
        originalRequest.headers[
          "Authorization"
        ] = `Bearer ${response.data.access}`;

        // Process any queued requests with new token
        processQueue(null, response.data.access);

        // Clean up
        isRefreshing = false;
        refreshPromise = null;

        // Retry the original request with new token
        return api(originalRequest);
      } catch (refreshError) {
        // Refresh token failed
        // console.error("Failed to refresh token", refreshError);

        // Clear tokens
        localStorage.setItem("access", "");
        localStorage.setItem("refresh", "");

        // Fail all queued requests
        processQueue(refreshError as AxiosError, null);

        // Clean up
        isRefreshing = false;
        refreshPromise = null;

        // Redirect to login
        window.location.href = "/login";

        return Promise.reject(refreshError);
      }
    }

    // Return other errors
    return Promise.reject(error);
  }
);

export default api;
